// Time pickers made by time_of_day_field (data-time-step): snap a typed or picked time to the step
// (5 minutes) and keep it within the field's min and max. The server applies the same rule.
//
// Fields with data-day-from also follow the department's working hours (data-working-hours) for the
// day set by another field: a date input, or a weekday select (value = Date#wday). The selector is
// looked up in the document, or within the field's .nested-fields row when it starts with "nested:".
// On a day the department doesn't work the field is made read-only with a hint (it still submits, so
// the server's "does not work on ..." message is what the user sees if they save anyway).
import $ from 'jquery/dist/jquery';

const toMinutes = (value) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(value || '');
  return match ? (Number(match[1]) * 60) + Number(match[2]) : null;
};
const toClock = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const snap = (field) => {
  const minutes = toMinutes(field.value);
  if (minutes === null) { return; }
  const step = Number(field.dataset.timeStep) || 5;
  const min = toMinutes(field.min);
  const max = toMinutes(field.max);
  let snapped = Math.round(minutes / step) * step;
  if (min !== null) { snapped = Math.max(snapped, min); }
  if (max !== null) { snapped = Math.min(snapped, max); }
  if (snapped !== minutes) {
    field.value = toClock(snapped);
    field.dispatchEvent(new Event('input', { bubbles: true })); // e.g. the supervision form's duration
  }
};

const daySource = (field) => {
  const selector = field.dataset.dayFrom;
  if (selector.startsWith('nested:')) {
    const row = field.closest('.nested-fields');
    return row ? row.querySelector(selector.slice(7)) : null;
  }
  return document.querySelector(selector);
};

// Date#getDay of the source's value, or null when no day is chosen
const weekdayOf = (source) => {
  if (!source || !source.value) { return null; }
  if (source.type === 'date') {
    const date = new Date(`${source.value}T00:00`);
    return Number.isNaN(date.getTime()) ? null : date.getDay();
  }
  const wday = Number(source.value);
  return Number.isNaN(wday) ? null : wday;
};

const hintFor = (field) => {
  let hint = field.parentElement.querySelector('.working-hours-hint');
  if (!hint) {
    hint = document.createElement('div');
    hint.className = 'form-text text-danger working-hours-hint';
    field.insertAdjacentElement('afterend', hint);
  }
  return hint;
};

const applyWorkingHours = (field) => {
  let hours;
  try { hours = JSON.parse(field.dataset.workingHours); } catch (e) { return; }
  if (!field.dataset.widestMin) {
    field.dataset.widestMin = field.min;
    field.dataset.widestMax = field.max;
  }
  const wday = weekdayOf(daySource(field));
  const hint = field.parentElement.querySelector('.working-hours-hint');

  if (wday !== null && !hours.workDays.includes(wday)) {
    field.readOnly = true;
    hintFor(field).textContent = `${field.dataset.department || 'This department'} does not work on ${DAY_NAMES[wday]}s`;
    return;
  }

  field.readOnly = false;
  if (hint) { hint.remove(); }
  const dayWindow = wday === null ? [field.dataset.widestMin, field.dataset.widestMax] : (hours.days[wday] || hours.default);
  [field.min, field.max] = dayWindow;
  snap(field);
};

const linkedFields = (source) => Array.from(document.querySelectorAll('input[type="time"][data-day-from]'))
  .filter((field) => daySource(field) === source);

document.addEventListener('change', (event) => {
  const field = event.target;
  if (!field.matches) { return; }
  if (field.matches('input[type="time"][data-time-step]')) {
    snap(field);
    return;
  }
  linkedFields(field).forEach(applyWorkingHours);
}, true);

const applyAll = () => document.querySelectorAll('input[type="time"][data-day-from]').forEach(applyWorkingHours);
document.addEventListener('turbolinks:load', applyAll);
$(document).on('cocoon:after-insert', applyAll); // slot rows added by cocoon (a jQuery event)
